import { Button, ButtonProps, PlacementWithLogical } from '@chakra-ui/react'
import { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { Tooltip } from '../Tooltip'
import { ACTIONS, ActionKind } from './actions'

export interface IActionFabProps extends Omit<ButtonProps, 'aria-label' | 'children'> {
  /** Ação do FAB. A grande maioria das listagens usa o padrão `create`. */
  action?: ActionKind
  label?: string
  /** Mesmo escape hatch do ActionButton: ações pontuais que não merecem entrada no registry. */
  icon?: ReactElement
  /** Posição na pilha de FABs, de baixo para cima. 0 = mais próximo do rodapé. */
  stackIndex?: number
  tooltipPlacement?: PlacementWithLogical
  /** Navegação interna. Use no lugar de embrulhar o FAB num `<Link>`, que aninha button dentro de anchor. */
  to?: string
  /** Abre a rota de `to` em nova aba, mantendo a listagem atual aberta. */
  openInNewTab?: boolean
  /** Link externo — abre em nova aba. */
  href?: string
}

const FAB_SIZE = '70px'
const FAB_EDGE_OFFSET = 20
const FAB_STACK_GAP = 80
// Explícito: como width/height são sobrescritos, herdar o fontSize da escala de
// `size` do Button deixaria o ícone em 16px (default md) e visivelmente fino.
const FAB_ICON_SIZE = 'lg'

/**
 * Botão flutuante fixo no canto inferior direito, usado para a ação principal de uma listagem.
 * Geometria única para todo o painel — use `stackIndex` quando a tela tiver mais de um FAB.
 */
export default function ActionFab({
  action = 'create',
  label,
  icon,
  stackIndex = 0,
  tooltipPlacement = 'left',
  to,
  openInNewTab,
  href,
  ...rest
}: IActionFabProps) {
  const config = ACTIONS[action]
  const text = label ?? config.label
  const glyph = icon ?? config.icon

  const noUnderline = { textDecoration: 'none', _hover: { textDecoration: 'none' } }
  const linkProps = to
    ? {
        as: Link,
        to,
        ...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
        ...noUnderline
      }
    : href
      ? { as: 'a', href, target: '_blank', rel: 'noopener noreferrer', ...noUnderline }
      : {}

  return (
    <Tooltip label={text} placement={tooltipPlacement}>
      <Button
        aria-label={text}
        colorScheme={config.colorScheme}
        variant={config.variant}
        position="fixed"
        bottom={`${FAB_EDGE_OFFSET + stackIndex * FAB_STACK_GAP}px`}
        right={`${FAB_EDGE_OFFSET}px`}
        borderRadius="full"
        width={FAB_SIZE}
        height={FAB_SIZE}
        fontSize={FAB_ICON_SIZE}
        boxShadow="lg"
        _hover={{ boxShadow: 'xl' }}
        _active={{ boxShadow: 'md' }}
        {...linkProps}
        {...rest}
      >
        {glyph}
      </Button>
    </Tooltip>
  )
}
